import { useEffect, useRef, useState } from 'react';
import { Loader2, Send, AlertTriangle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useIsInfoAssistantVisible } from '@/contexts/FeatureFlagContext';
import {
  deleteAssistantSession,
  fetchAssistantSessionMessages,
  formatApiError,
  persistActiveAssistantSessionId,
  pollAssistantAnswer,
  readActiveAssistantSessionId,
  sendSapNotesMessage,
} from '@/lib/assistantApi';

/** No literal "angel" glyph in lucide-react — a small custom icon (halo + wings +
 * head) matching lucide's stroke conventions (24x24, currentColor, round caps). */
function AngelIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <ellipse cx="12" cy="4.3" rx="4.4" ry="2.6" />
      <circle cx="12" cy="11" r="3.4" fill="currentColor" stroke="none" />
      <path d="M8.8 13.2C5.5 14.3 3 17 2.3 21" />
      <path d="M15.2 13.2C18.5 14.3 21 17 21.7 21" />
      <path d="M12 14.4v6.6" />
    </svg>
  );
}

function ChatBubble({ role, content, citedNoteIds = [] }) {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg border px-3 py-2 text-sm ${
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'
        }`}
      >
        <p className="whitespace-pre-wrap">{content}</p>
        {citedNoteIds.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {citedNoteIds.map((id) => (
              <Badge key={id} variant="secondary" data-testid="info-assistant-citation">
                SAP Note {id}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function InfoAssistantWidget() {
  const visible = useIsInfoAssistantVisible();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => readActiveAssistantSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const scrollRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open || !sessionId) return;
    fetchAssistantSessionMessages(sessionId)
      .then((data) => {
        const restored = (data.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          citedNoteIds: [],
        }));
        if (restored.length > 0) setMessages(restored);
      })
      .catch(() => {
        /* best-effort restore; ignore failures */
      });
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  if (!visible) return null;

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setAuthError(false);
    setMessages((prev) => [...prev, { role: 'user', content: text, citedNoteIds: [] }]);
    setLoading(true);
    try {
      const ack = await sendSapNotesMessage({ sessionId, message: text });
      setSessionId(ack.session_id);
      persistActiveAssistantSessionId(ack.session_id);

      if (ack.status === 'failed') {
        if (ack.error_code === 'mcp_auth_error') setAuthError(true);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'That request could not be started. Please try again.', citedNoteIds: [] },
        ]);
        return;
      }

      // Search + synthesis against the SAP Notes MCP gateway routinely takes
      // 60-90s (well past API Gateway's 29s ceiling), which is why the POST
      // above returns immediately — poll until the background job finishes.
      const polled = await pollAssistantAnswer(ack.session_id, { intervalMs: 2000, timeoutMs: 120000 });
      if (!mountedRef.current) return;

      if (polled.status !== 'done' && polled.status !== 'failed') {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: "This is taking longer than usual — it's still working in the background. Try reopening this session in a moment.",
            citedNoteIds: [],
          },
        ]);
        return;
      }

      const latest = polled.latest || {};
      if (latest.error_code === 'mcp_auth_error') setAuthError(true);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: latest.answer || '', citedNoteIds: latest.cited_note_ids || [] },
      ]);
    } catch (err) {
      if (!mountedRef.current) return;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: formatApiError(err), citedNoteIds: [] },
      ]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Closing the panel wipes this session's stored data — same "don't keep it
  // around" intent as the logout hook in AuthContext.js. Best-effort: a
  // delete failure shouldn't stop the panel from closing or block reopening
  // it fresh next time.
  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen) return;
    const idToDelete = sessionId;
    setMessages([]);
    setSessionId('');
    setAuthError(false);
    persistActiveAssistantSessionId('');
    if (idToDelete) {
      deleteAssistantSession(idToDelete).catch(() => {
        /* best-effort cleanup; nothing to surface to the user */
      });
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 h-16 w-16 rounded-full shadow-lg p-0"
        data-testid="info-assistant-trigger"
        aria-label="Open info assistant"
      >
        <AngelIcon className="h-10 w-10" />
      </Button>

      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="flex flex-col w-full sm:max-w-md p-0"
          data-testid="info-assistant-panel"
        >
          <SheetHeader className="p-4 border-b text-left">
            <SheetTitle>Angel</SheetTitle>
            <SheetDescription>
              Your AI assistant — chat, or ask about SAP errors, patches, and troubleshooting
              sourced from official SAP Notes.
            </SheetDescription>
          </SheetHeader>

          {authError && (
            <div
              className="mx-4 mt-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
              data-testid="info-assistant-auth-error"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>SAP Notes lookup is temporarily unavailable — please contact your administrator.</span>
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4">
            <div className="flex flex-col gap-3 py-4">
              {messages.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Say hi, or try: "How do I fix short dump TIME_OUT in background jobs?"
                </p>
              )}
              {messages.map((m, i) => (
                <ChatBubble key={i} role={m.role} content={m.content} citedNoteIds={m.citedNoteIds} />
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Searching SAP Notes... this can take up to a minute
                </div>
              )}
            </div>
          </div>

          <div className="flex items-end gap-2 border-t p-4">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question..."
              rows={2}
              className="resize-none"
              data-testid="info-assistant-input"
            />
            <Button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              size="icon"
              data-testid="info-assistant-send"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
